import type { Api } from "../../services/api";
import { ProfilePictureUser } from "../../components/ProfilePicture";

type PublisherKind =
  "journal" | "congress" | "company" | "user" | "owner" | "unknown";
type PublisherKindExplicit = Exclude<
  PublisherKind,
  "owner" | "user" | "unknown"
>;

export type VideoPublisherDisplay = {
  kind: PublisherKind;
  path: string | null;
  image: string | null;
  name: string | null;
  userProfile: ProfilePictureUser | null;
};

type VideoDetail = Extract<
  Api["get"]["/webapp/public/content/:id"]["output"]["body"],
  { content_format: "Video" }
>;
type VideoListItem = Extract<
  Api["get"]["/webapp/public/content"]["output"]["body"]["docs"][number],
  { content_format: "Video" }
>;
export type VideoPublisherSource = VideoListItem | VideoDetail;

/**
 * Minimal shape needed to resolve the publisher display.
 * Wider than VideoPublisherSource so lightweight endpoint types (e.g. room carousel) can use it.
 */
type PublisherInput = {
  publisher: string | null;
  journal: { uid: string; name: string; image: { url: string } | null } | null;
  congress: { name: string; image: { url: string } | null } | null;
  company: { id: string; name: string; logo: { url: string } } | null;
  owner: { name: string; logo: { url: string } } | null;
};

function fromJournal(video: PublisherInput): VideoPublisherDisplay | null {
  if (video.journal === null) return null;
  return {
    kind: "journal",
    path: `/journal/${video.journal.uid}`,
    image: video.journal.image === null ? null : video.journal.image.url,
    name: video.journal.name,
    userProfile: null,
  };
}

function fromCongress(video: PublisherInput): VideoPublisherDisplay | null {
  if (video.congress === null) return null;
  return {
    kind: "congress",
    path: null,
    image: video.congress.image === null ? null : video.congress.image.url,
    name: video.congress.name,
    userProfile: null,
  };
}

function fromCompany(video: PublisherInput): VideoPublisherDisplay | null {
  if (video.company === null) return null;
  return {
    kind: "company",
    path: `/company/${video.company.id}`,
    image: video.company.logo.url,
    name: video.company.name,
    userProfile: null,
  };
}

function fromOwner(video: PublisherInput): VideoPublisherDisplay | null {
  if (video.owner === null) return null;
  return {
    kind: "owner",
    path: null,
    image: video.owner.logo.url,
    name: video.owner.name,
    userProfile: null,
  };
}

export function resolveVideoPublisherDisplay(
  video: PublisherInput
): VideoPublisherDisplay {
  const pickersByPublisher: Record<
    PublisherKindExplicit,
    (video: PublisherInput) => VideoPublisherDisplay | null
  > = {
    journal: fromJournal,
    congress: fromCongress,
    company: fromCompany,
  };

  const byExplicitPublisher =
    video.publisher === "journal" ||
    video.publisher === "congress" ||
    video.publisher === "company"
      ? pickersByPublisher[video.publisher]
      : undefined;
  const explicitMatch = byExplicitPublisher?.(video);
  if (explicitMatch !== null && explicitMatch !== undefined) {
    return explicitMatch;
  }

  return (
    fromJournal(video) ??
    fromCongress(video) ??
    fromCompany(video) ??
    fromOwner(video) ?? {
      kind: "unknown",
      path: null,
      image: null,
      name: null,
      userProfile: null,
    }
  );
}
